import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Col, Form, Nav, Row, Tab } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import { FormMsg, NoFormMsg, doFormMsg } from 'components/forms/FormMessage';
import { ModalDialog, ModalDialogLink } from 'components/modal/Modal';
import { setDebugPanel } from 'features/admin/adminSlice';
import { IAppSettings } from 'features/admin/appSettingsAPI';
import {
  emptySettings,
  patchAppSettingsAsync,
} from 'features/admin/appSettingsSlice';
import { getFileTypeInfo } from 'features/files/fileSlice';
import api from 'helpers/api';
import { useAdmin } from 'selectors/adminSelectors';
import { useAppSettings } from 'selectors/appSettingsSelectors';
import { useUser } from 'selectors/userSelectors';

import AdminMaintenanceMode from './AdminMaintenanceMode';

type MaintenanceModeStatus = {
  maintenanceMode: boolean;
};

export default function AppSettingsForm() {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { userIsAdmin } = useUser();
  const { settings } = useAppSettings();
  const { debugPanel } = useAdmin();

  const [data, setData] = useState<IAppSettings>(emptySettings);
  const listsRef = useRef<HTMLDivElement>(null);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false);

  enum settingsTabs {
    rights = 'rights',
    tools = 'tools',
    uploads = 'uploads',
    config = 'config',
  }
  const [activeTab, setActiveTab] = useState<settingsTabs>(
    settingsTabs.uploads,
  );

  useEffect(() => {
    if (settings) {
      setData({
        ...settings,
        rights: settings.rights ?? {},
        maxmbs: settings.maxmbs ?? {},
        config: settings.config ?? {},
      });
    }
  }, []);

  useEffect(() => {
    api
      .get('admin/maintenance')
      .then((response) => setMaintenanceMode(response.data.maintenanceMode))
      .catch((error) =>
        console.error('Failed to fetch maintenance mode status:', error),
      );
  }, []);

  if (!userIsAdmin) return null;

  function handleCheckRights(name: string, checked: boolean) {
    setData({
      ...data,
      rights: { ...data.rights, [name]: !checked },
    });
  }

  function handleChangeRights(e: SyntheticEvent) {
    const target = e.target as HTMLInputElement;
    setData({
      ...data,
      rights: { ...data.rights, [target.name]: target.value },
    });
  }

  function handleChangeMaxMbs(e: SyntheticEvent) {
    const target = e.target as HTMLInputElement;
    const val: number = target.valueAsNumber >= 0 ? target.valueAsNumber : 0;
    setData({
      ...data,
      maxmbs: { ...data.maxmbs, [target.name]: val },
    });
  }

  async function handleSubmit(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        for (const key in data.maxmbs) {
          if (data.maxmbs.hasOwnProperty(key) && data.maxmbs[key] <= 0) {
            data.maxmbs[key] = undefined;
          }
        }
        await dispatch(patchAppSettingsAsync({ data })).then(
          (response: { payload: any }) => {
            switch (response.payload?.response.status) {
              case 200:
              case 201:
                resolve(true);
                break;
              case 401:
              default:
                doFormMsg(
                  {
                    message: intl.formatMessage({ id: 'X.FAILED' }),
                    success: false,
                  },
                  setFormMsg,
                );
                reject(intl.formatMessage({ id: 'X.FAILED' }));
                break;
            }
          },
        );
      } catch (err) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
            success: false,
          },
          setFormMsg,
        );
        reject(intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }));
      }
    });
  }

  function toolName(key: string): string {
    return intl.formatMessage({ id: `TOOLS.${key.toUpperCase()}` });
  }

  function handleCheckConfig(name: string, checked: boolean) {
    if (data) {
      const [section, key] = name.split('.');
      if (section && key) {
        setData({
          ...data,
          config: {
            ...data.config,
            [section]: {
              ...data.config?.[section],
              [key]: !checked,
            },
          },
        });
      }
    }
  }

  function handleCheckDebugPanel(name: string, checked: boolean) {
    dispatch(setDebugPanel(!checked));
  }

  return (
    <ModalDialog
      className="settings_form appsettings_form"
      title={<FormattedMessage id={'ADMIN.SETTINGS.FORM_TITLE'} />}
      okBtn={{
        onClick: handleSubmit,
      }}
      cancelBtn={{}}
      enableEscape={true}
      formMsg={formMsg}
      height={600}
    >
      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) =>
          setActiveTab((k as settingsTabs) || settingsTabs.uploads)
        }
      >
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey={settingsTabs.uploads}>
              <FormattedMessage id="ADMIN.SETTINGS.MAXMBS.UPLOADS" />
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={settingsTabs.config}>
              <FormattedMessage
                id="ADMIN.SETTINGS.CONFIG.TITLE"
                defaultMessage="Config"
              />
            </Nav.Link>
          </Nav.Item>
          {/* <Nav.Item>
            <Nav.Link eventKey={settingsTabs.rights}>
              <FormattedMessage id="ADMIN.SETTINGS.USERS.RIGHTS" />
            </Nav.Link>
          </Nav.Item> */}
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey={settingsTabs.rights}>
            {/* USER RIGHTS */}
            <div className="header">
              <div className="subheading">
                <FormattedMessage id="ADMIN.SETTINGS.USERS.RIGHTS" />
              </div>
            </div>
            {/* <Form.Group as={Row}>
              <Col sm={12} md={12} lg={12}>
                <SettingsCheckbox
                  key={'userCreateProject'}
                  name={'userCreateProject'}
                  label={intl.formatMessage({
                    id: 'ADMIN.SETTINGS.USERS.CREATE_PROJECT',
                  })}
                  checked={data?.rights?.userCreateProject ?? false}
                  onCheck={handleCheckRights}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row}>
              <Col sm={6} md={6} lg={6}>
                <SettingsCheckbox
                  key={'guestTryout'}
                  name={'guestTryout'}
                  label={intl.formatMessage({
                    id: 'ADMIN.SETTINGS.USERS.GUEST_TRYOUT',
                  })}
                  checked={data?.rights?.guestTryout ?? false}
                  onCheck={handleCheckRights}
                />
              </Col>
              <Col sm={6} md={6} lg={6}>
                <Form.Control
                  type="number"
                  name="tryoutDialogue"
                  value={data?.rights?.tryoutDialogue ?? 0}
                  onChange={handleChangeRights}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row}>
              <Col sm={6} md={6} lg={6} className="rightaligned">
                <FormattedMessage id="ADMIN.SETTINGS.USERS.GUEST_TRYOUT_CODE" />
                :
              </Col>
              <Col sm={6} md={6} lg={6}>
                <Form.Control
                  type="text"
                  name="tryoutCode"
                  value={data?.rights?.tryoutCode ?? ''}
                  onChange={handleChangeRights}
                />
              </Col>
            </Form.Group> */}
          </Tab.Pane>
          <Tab.Pane eventKey={settingsTabs.uploads}>
            {/* UPLOADS */}
            <div className="header">
              <div className="subheading">
                <FormattedMessage id="ADMIN.SETTINGS.MAXMBS.TITLE" />
              </div>
            </div>
            <Form.Group as={Row}>
              <Col sm={3} md={3} lg={3} className="rightaligned">
                <FormattedMessage id="ADMIN.SETTINGS.MAXMBS.IMG" />:
              </Col>
              <Col sm={2} md={2} lg={2}>
                <Form.Control
                  type="number"
                  name="img"
                  value={data?.maxmbs?.img ?? 0}
                  onChange={handleChangeMaxMbs}
                />
              </Col>
              {!data?.maxmbs?.img ? (
                <Col sm={6} md={6} lg={6}>
                  <FormattedMessage
                    id="ADMIN.SETTINGS.MAXMBS.DEFAULT"
                    values={{
                      default: getFileTypeInfo('img').size / 1_000_000,
                    }}
                  />
                  :
                </Col>
              ) : null}
            </Form.Group>
            <Form.Group as={Row}>
              <Col sm={3} md={3} lg={3} className="rightaligned">
                <FormattedMessage id="ADMIN.SETTINGS.MAXMBS.DOC" />:
              </Col>
              <Col sm={2} md={2} lg={2}>
                <Form.Control
                  type="number"
                  name="doc"
                  value={data?.maxmbs?.doc ?? 0}
                  onChange={handleChangeMaxMbs}
                />
              </Col>
              {!data?.maxmbs?.doc ? (
                <Col sm={6} md={6} lg={6}>
                  <FormattedMessage
                    id="ADMIN.SETTINGS.MAXMBS.DEFAULT"
                    values={{
                      default: getFileTypeInfo('doc').size / 1_000_000,
                    }}
                  />
                  :
                </Col>
              ) : null}
            </Form.Group>
            <Form.Group as={Row}>
              <Col sm={3} md={3} lg={3} className="rightaligned">
                <FormattedMessage id="ADMIN.SETTINGS.MAXMBS.XLS" />:
              </Col>
              <Col sm={2} md={2} lg={2}>
                <Form.Control
                  type="number"
                  name="xls"
                  value={data?.maxmbs?.xls ?? 0}
                  onChange={handleChangeMaxMbs}
                />
              </Col>
              {!data?.maxmbs?.xls ? (
                <Col sm={6} md={6} lg={6}>
                  <FormattedMessage
                    id="ADMIN.SETTINGS.MAXMBS.DEFAULT"
                    values={{
                      default: getFileTypeInfo('xls').size / 1_000_000,
                    }}
                  />
                  :
                </Col>
              ) : null}
            </Form.Group>
            <Form.Group as={Row}>
              <Col sm={3} md={3} lg={3} className="rightaligned">
                <FormattedMessage id="ADMIN.SETTINGS.MAXMBS.PPT" />:
              </Col>
              <Col sm={2} md={2} lg={2}>
                <Form.Control
                  type="number"
                  name="ppt"
                  value={data?.maxmbs?.ppt ?? 0}
                  onChange={handleChangeMaxMbs}
                />
              </Col>
              {!data?.maxmbs?.ppt ? (
                <Col sm={6} md={6} lg={6}>
                  <FormattedMessage
                    id="ADMIN.SETTINGS.MAXMBS.DEFAULT"
                    values={{
                      default: getFileTypeInfo('ppt').size / 1_000_000,
                    }}
                  />
                  :
                </Col>
              ) : null}
            </Form.Group>
            <Form.Group as={Row}>
              <Col sm={3} md={3} lg={3} className="rightaligned">
                <FormattedMessage id="ADMIN.SETTINGS.MAXMBS.PDF" />:
              </Col>
              <Col sm={2} md={2} lg={2}>
                <Form.Control
                  type="number"
                  name="pdf"
                  value={data?.maxmbs?.pdf ?? 0}
                  onChange={handleChangeMaxMbs}
                />
              </Col>
              {!data?.maxmbs?.pdf ? (
                <Col sm={6} md={6} lg={6}>
                  <FormattedMessage
                    id="ADMIN.SETTINGS.MAXMBS.DEFAULT"
                    values={{
                      default: getFileTypeInfo('pdf').size / 1_000_000,
                    }}
                  />
                  :
                </Col>
              ) : null}
            </Form.Group>
          </Tab.Pane>
          <Tab.Pane eventKey={settingsTabs.config}>
            {/* CONFIG TAB */}
            <div className="header">
              <div className="subheading">
                <FormattedMessage
                  id="ADMIN.SETTINGS.CONFIG.TITLE"
                  defaultMessage="Config"
                />
              </div>
            </div>
            {/* <Form.Group as={Row}>
              <Col sm={12} md={12} lg={12}>
                <SettingsCheckbox
                  key={'async.pings'}
                  name={'async.pings'}
                  label={intl.formatMessage({
                    id: 'ADMIN.SETTINGS.CONFIG.ASYNC_PINGS',
                  })}
                  checked={data?.config?.async?.pings ?? false}
                  onCheck={handleCheckConfig}
                />
              </Col>
            </Form.Group> */}
            {/* <Form.Group as={Row}>
              <Col sm={12} md={12} lg={12}>
                <SettingsCheckbox
                  key={'notifications.enabled'}
                  name={'notifications.enabled'}
                  label={intl.formatMessage({
                    id: 'ADMIN.SETTINGS.CONFIG.NOTIFICATIONS_ENABLED',
                  })}
                  checked={data?.config?.notifications?.enabled ?? false}
                  onCheck={handleCheckConfig}
                />
              </Col>
            </Form.Group> */}
            <Form.Group as={Row}>
              <Col sm={12} md={12} lg={12}>
                <SettingsCheckbox
                  key={'debugPanel'}
                  name={'debugPanel'}
                  label={intl.formatMessage({
                    id: 'ADMIN.SETTINGS.CONFIG.DEBUG_PANEL',
                  })}
                  checked={debugPanel}
                  onCheck={handleCheckDebugPanel}
                />
              </Col>
            </Form.Group>
            <hr />
            <ModalDialogLink
              component={AdminMaintenanceMode}
              asButton={true}
              closeAll={true}
              style={
                maintenanceMode ? { color: 'red', fontWeight: 'bold' } : {}
              }
            >
              <FormattedMessage
                id="ADMIN.SETTINGS.CONFIG.MAINTENANCE_MODE"
                values={{ mode: maintenanceMode ? 'enabled' : 'disabled' }}
              />
            </ModalDialogLink>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </ModalDialog>
  );
}

type SettingsCheckboxProps = {
  name: string;
  label: string;
  checked: boolean;
  onCheck: (name: string, checked: boolean) => void;
  disabled?: boolean;
};

export function SettingsCheckbox(props: SettingsCheckboxProps) {
  const { name, label, checked, onCheck, disabled = false } = props;

  function handleChange(event: SyntheticEvent) {
    onCheck(name, checked);
  }

  return (
    <Form.Group controlId={name}>
      <Form.Check
        className="checkbox"
        checked={checked}
        name={name}
        label={label}
        onChange={handleChange}
        type={'checkbox'}
        disabled={disabled}
      />
    </Form.Group>
  );
}
